import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X, Copy, Check, Share2, ExternalLink,
  MessageCircle, Link2
} from 'lucide-react';
import { Button } from './Button';
import { Card, CardContent, CardHeader, CardTitle } from './Card';
import toast from 'react-hot-toast';

const ShareModal = ({ project, isOpen, onClose }) => {
  const [copiedItem, setCopiedItem] = useState(null);
  const [shareUrl, setShareUrl] = useState('');

  useEffect(() => {
    if (isOpen && project) {
      // Use current URL or construct project URL
      const url = window.location.href;
      setShareUrl(url);
    }
  }, [isOpen, project]);

  // Modern copy to clipboard function
  const copyToClipboard = async (text, itemType = 'url') => {
    try {
      // Check if clipboard API is available
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text);
      } else {
        // For non-secure contexts, show a fallback message
        toast.error('Copy feature requires HTTPS. Please copy the URL manually.');
        return;
      }

      // Show success feedback
      setCopiedItem(itemType);
      toast.success(
        itemType === 'url' ? 'Project link copied!' : 'Text copied!',
        { duration: 2000 }
      );

      // Reset copied state after 2 seconds
      setTimeout(() => setCopiedItem(null), 2000);

    } catch (error) {
      console.error('Copy failed:', error);
      toast.error('Failed to copy. Please try selecting and copying manually.');
    }
  };

  // Generate share text
  const getShareText = () => {
    if (!project) return '';
    return `Check out "${project.title}" on CrowBlock! ${project.story?.substring(0, 100)}...`;
  };

  // Social sharing URLs
  const getSocialShareUrl = (platform) => {
    const text = encodeURIComponent(getShareText());
    const url = encodeURIComponent(shareUrl);
    
    const urls = {
      twitter: `https://twitter.com/intent/tweet?text=${text}&url=${url}`,
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${url}`,
      whatsapp: `https://wa.me/?text=${text}%20${url}`,

    };
    
    return urls[platform];
  };

  // Handle social share
  const handleSocialShare = (platform) => {
    const url = getSocialShareUrl(platform);
    window.open(url, '_blank', 'width=600,height=400,scrollbars=yes,resizable=yes');
  };

  // Handle manual selection for copy
  const handleUrlSelect = (e) => {
    e.target.select();
    e.target.setSelectionRange(0, 99999); // For mobile devices
  };

  if (!isOpen || !project) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          transition={{ type: "spring", duration: 0.5 }}
          className="w-full max-w-lg mx-auto"
          onClick={(e) => e.stopPropagation()}
        >
          <Card className="border-0 shadow-2xl bg-white/95 backdrop-blur-md">
            <CardHeader className="pb-4">
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2 text-xl">
                  <div className="w-8 h-8 bg-primary-teal/10 rounded-lg flex items-center justify-center">
                    <Share2 className="w-5 h-5 text-primary-teal" />
                  </div>
                  Share Project
                </CardTitle>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={onClose}
                  className="h-8 w-8 rounded-full hover:bg-neutral-100"
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
              <p className="text-sm text-neutral-600 mt-2">
                Share "{project.title}" with others
              </p>
            </CardHeader>

            <CardContent className="space-y-6">
              {/* Project URL Copy Section */}
              <div>
                <h3 className="font-semibold mb-3 text-sm text-neutral-700">Project Link</h3>
                <div className="flex items-center gap-2 p-3 bg-neutral-50 rounded-xl border">
                  <Link2 className="w-4 h-4 text-neutral-500 flex-shrink-0" />
                  <input
                    type="text"
                    value={shareUrl}
                    readOnly
                    className="flex-1 bg-transparent text-sm text-neutral-700 focus:outline-none cursor-pointer"
                    onClick={handleUrlSelect}
                    title="Click to select all text"
                  />
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => copyToClipboard(shareUrl, 'url')}
                    className="h-8 px-3 hover:bg-primary-teal/10 hover:text-primary-teal"
                  >
                    {copiedItem === 'url' ? (
                      <Check className="w-4 h-4 text-green-600" />
                    ) : (
                      <Copy className="w-4 h-4" />
                    )}
                  </Button>
                </div>
                
              </div>

              {/* Share Text Copy Section */}
              <div>
                <h3 className="font-semibold mb-3 text-sm text-neutral-700">Share Text</h3>
                <div className="p-3 bg-neutral-50 rounded-xl border">
                  <textarea
                    value={getShareText()}
                    readOnly
                    rows={3}
                    className="w-full bg-transparent text-sm text-neutral-700 focus:outline-none resize-none cursor-pointer"
                    onClick={handleUrlSelect}
                    title="Click to select all text"
                  />
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => copyToClipboard(getShareText(), 'text')}
                    className="w-full h-8 mt-2"
                  >
                    {copiedItem === 'text' ? (
                      <>
                        <Check className="w-4 h-4 mr-2 text-green-600" />
                        Copied!
                      </>
                    ) : (
                      <>
                        <Copy className="w-4 h-4 mr-2" />
                        Copy Text
                      </>
                    )}
                  </Button>
                </div>
              </div>

              {/* Social Sharing Options */}
              <div>
                <h3 className="font-semibold mb-3 text-sm text-neutral-700">Share On</h3>
                <div className="grid grid-cols-3 gap-3">
                  <Button
                    variant="outline"
                    onClick={() => handleSocialShare('twitter')}
                    className="h-12 bg-neutral-50 border-neutral-200 hover:bg-neutral-100 hover:border-neutral-300"
                  >
                    <X className="w-5 h-5 mr-2 text-neutral-700" />
                  </Button>
                  
                  <Button
                    variant="outline"
                    onClick={() => handleSocialShare('facebook')}
                    className="h-12 bg-blue-50 border-blue-200 hover:bg-blue-100 hover:border-blue-300"
                  >
                    Meta
                  </Button>
                  
                  <Button
                    variant="outline"
                    onClick={() => handleSocialShare('whatsapp')}
                    className="h-12 bg-green-50 border-green-200 hover:bg-green-100 hover:border-green-300"
                  >
                    <MessageCircle className="w-5 h-5 mr-2 text-green-600" />
                   
                  </Button>
                </div>
              </div>

              {/* Quick Copy Actions */}
              <div className="flex gap-3 pt-2">
                <Button 
                  variant="outline" 
                  onClick={onClose}
                  className="flex-1"
                >
                  Close
                </Button>
                <Button 
                  onClick={() => copyToClipboard(shareUrl, 'url')}
                  className="flex-1 bg-primary-teal hover:bg-primary-teal/90"
                >
                  {copiedItem === 'url' ? (
                    <>
                      <Check className="w-4 h-4 mr-2" />
                      Copied!
                    </>
                  ) : (
                    <>
                      <Copy className="w-4 h-4 mr-2" />
                      Copy Link
                    </>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default ShareModal;
